import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Trophy, Search, Calendar, Users, Target, Medal, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

/**
 * Detect whether West Ham is the home team for a given fixture.
 *
 * Trust `home_away` first (semantic: "Home" | "Away"). Only fall back to
 * `venue` when it explicitly says "Home"/"Away". Do NOT match on
 * "West Ham" / "London Stadium" appearing in the venue string — that gives
 * false positives for football-data.org fixtures which set
 * `venue: "West Ham United FC"` for BOTH home AND away games.
 */
function isHomeGame({ home_away, fixture_home_away, venue, fixture_venue }) {
  const raw = (home_away || fixture_home_away || venue || fixture_venue || "").toString().toLowerCase();
  if (/^home$/i.test(raw)) return true;
  if (/^away$/i.test(raw)) return false;
  // Ambiguous (e.g. stadium name) → default to Home to preserve legacy
  // behaviour for records that pre-date the home_away field being set.
  return true;
}

export default function ScorePredictorPage() {
  const { user } = useAuth();
  const [upcomingFixtures, setUpcomingFixtures] = useState([]);
  // scores[fixture_id] = { home, away, submitting, message, saved }
  const [scores, setScores] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [myPredictions, setMyPredictions] = useState([]);
  const [myTotalPoints, setMyTotalPoints] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("2026-27");
  const [seasons, setSeasons] = useState(["2026-27", "2025-26", "2024-25"]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // Refresh every 60s so a fixture drops off after kickoff and the next one appears.
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [selectedSeason, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

      // Fetch next 5 upcoming fixtures (with existing predictions if logged in)
      const upcomingRes = await axios.get(
        `${API}/score-predictor/upcoming-fixtures`,
        { headers: authHeader }
      );
      const list = upcomingRes.data.fixtures || [];
      setUpcomingFixtures(list);
      // Preload the scores state from user's existing prediction (or 0-0)
      setScores(prev => {
        const next = {};
        for (const entry of list) {
          const fid = entry.fixture.id;
          const existing = prev[fid];
          if (existing && existing.dirty) {
            // preserve unsaved edits on refresh
            next[fid] = existing;
          } else if (entry.my_prediction) {
            next[fid] = {
              home: entry.my_prediction.predicted_home_score,
              away: entry.my_prediction.predicted_away_score,
              saved: true,
              submitting: false,
              message: "",
              dirty: false,
            };
          } else {
            next[fid] = { home: 0, away: 0, saved: false, submitting: false, message: "", dirty: false };
          }
        }
        return next;
      });

      // Fetch leaderboard
      const leaderboardRes = await axios.get(`${API}/score-predictor/leaderboard?season=${selectedSeason}`);
      setLeaderboard(leaderboardRes.data.leaderboard);

      // Fetch seasons
      const seasonsRes = await axios.get(`${API}/score-predictor/seasons`);
      const apiSeasons = (seasonsRes.data.seasons || []).map(s =>
        typeof s === 'object' ? s.season : s
      );
      // Always guarantee the current and last two seasons remain selectable.
      const merged = Array.from(new Set([
        seasonsRes.data.current_season || "2026-27",
        ...apiSeasons,
        "2026-27",
        "2025-26",
        "2024-25",
      ].filter(Boolean)));
      merged.sort().reverse();
      setSeasons(merged);

      // Fetch my predictions if logged in
      if (user) {
        const myPredRes = await axios.get(`${API}/score-predictor/my-predictions?season=${selectedSeason}`, {
          headers: authHeader
        });
        setMyPredictions(myPredRes.data.predictions);
        setMyTotalPoints(myPredRes.data.total_points);
      } else {
        setMyPredictions([]);
        setMyTotalPoints(0);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const bumpScore = (fixtureId, side, delta) => {
    setScores(prev => {
      const cur = prev[fixtureId] || { home: 0, away: 0 };
      const nextVal = Math.max(0, (cur[side] || 0) + delta);
      return { ...prev, [fixtureId]: { ...cur, [side]: nextVal, dirty: true, message: "" } };
    });
  };

  const submitPredictionFor = async (fixture) => {
    if (!user) {
      setScores(prev => ({
        ...prev,
        [fixture.id]: { ...(prev[fixture.id] || { home: 0, away: 0 }), message: "Please log in to submit predictions" },
      }));
      return;
    }
    const entry = scores[fixture.id] || { home: 0, away: 0 };
    setScores(prev => ({ ...prev, [fixture.id]: { ...entry, submitting: true, message: "" } }));
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/score-predictor/predict`, {
        fixture_id: fixture.id,
        predicted_home_score: entry.home,
        predicted_away_score: entry.away,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setScores(prev => ({
        ...prev,
        [fixture.id]: { ...entry, submitting: false, saved: true, dirty: false, message: "Saved!" },
      }));
      // Refresh myPredictions/points in background
      fetchData();
    } catch (error) {
      setScores(prev => ({
        ...prev,
        [fixture.id]: { ...entry, submitting: false, message: error.response?.data?.detail || "Failed to submit prediction" },
      }));
    }
  };

  const searchUser = async () => {
    if (searchQuery.length < 2) return;
    try {
      const res = await axios.get(`${API}/score-predictor/search-user?name=${searchQuery}&season=${selectedSeason}`);
      setSearchResults(res.data.results);
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Medal className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-sm font-bold text-slate-500">{rank}</span>;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="score-predictor-page">
      <SEO title="Score Predictor" path="/score-predictor" description="Predict West Ham United match scores and compete against other fans. Join the prediction league and prove your footballing knowledge." />
      <Navbar />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#7C2C3B] to-[#2BAEE5] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <Trophy className="w-10 h-10 text-yellow-400" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                ALL WHUFC SCORE PREDICTION LEAGUE
              </h1>
              <p className="text-white/80">Predict the score, earn points, climb the leaderboard!</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Season Selector */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-slate-600">Season:</span>
            <Select value={selectedSeason} onValueChange={setSelectedSeason}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {seasons.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {user && (
            <div className="bg-[#7C2C3B] text-white px-4 py-2 rounded-lg">
              <span className="text-sm">Your Points:</span>
              <span className="ml-2 text-xl font-bold">{myTotalPoints}</span>
            </div>
          )}
        </div>

        <Tabs defaultValue="predict" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="predict" data-testid="tab-predict">
              <Target className="w-4 h-4 mr-2" />
              Predict
            </TabsTrigger>
            <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">
              <Trophy className="w-4 h-4 mr-2" />
              Leaderboard
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <Calendar className="w-4 h-4 mr-2" />
              My History
            </TabsTrigger>
          </TabsList>

          {/* Predict Tab */}
          <TabsContent value="predict">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="predict-tab-panel">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                  NEXT 5 FIXTURES
                </h2>
                <p className="text-xs text-slate-500">
                  Predictions lock at kickoff. You can update your pick any time before the game starts.
                </p>
              </div>

              {upcomingFixtures.length === 0 ? (
                <p className="text-center text-slate-500 py-8" data-testid="no-upcoming-fixtures">
                  {loading ? "Loading fixtures..." : "No upcoming fixtures available for prediction"}
                </p>
              ) : (
                <div className="space-y-4">
                  {upcomingFixtures.map((entry, idx) => {
                    const fx = entry.fixture;
                    const s = scores[fx.id] || { home: 0, away: 0, submitting: false, message: "", saved: false, dirty: false };
                    const isHome = isHomeGame(fx);
                    const homeTeam = isHome ? 'West Ham' : fx.opponent;
                    const awayTeam = isHome ? fx.opponent : 'West Ham';
                    const kickoff = new Date(fx.date);
                    const dateLabel = kickoff.toLocaleDateString('en-GB', {
                      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
                    });
                    const timeLabel = fx.time || kickoff.toLocaleTimeString('en-GB', {
                      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London'
                    });
                    return (
                      <div
                        key={fx.id}
                        className={`border rounded-lg p-4 ${idx === 0 ? 'border-[#7C2C3B]/40 bg-[#7C2C3B]/5' : 'border-slate-200 bg-slate-50'}`}
                        data-testid={`fixture-card-${fx.id}`}
                      >
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            {idx === 0 && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#7C2C3B] text-white uppercase tracking-wide">
                                Next Up
                              </span>
                            )}
                            <span className="badge-claret px-2 py-0.5 rounded-full text-xs">
                              {fx.competition}
                            </span>
                            <span className="text-xs text-slate-500">
                              {dateLabel} · {timeLabel}
                            </span>
                          </div>
                          {s.saved && !s.dirty && (
                            <span className="text-xs font-semibold text-green-700" data-testid={`saved-badge-${fx.id}`}>
                              ✓ Prediction saved
                            </span>
                          )}
                        </div>

                        <div className="flex items-center justify-center gap-6 flex-wrap">
                          <div className="text-center min-w-[110px]">
                            <p className={`text-base font-bold ${homeTeam === 'West Ham' ? 'text-[#7C2C3B]' : 'text-slate-700'}`} data-testid={`home-team-${fx.id}`}>
                              {homeTeam}
                            </p>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Home</p>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="flex flex-col items-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => bumpScore(fx.id, 'home', 1)}
                                data-testid={`home-up-${fx.id}`}
                              >
                                <ChevronUp className="w-4 h-4" />
                              </Button>
                              <span className="text-3xl font-bold text-[#0F172A] my-1" data-testid={`home-score-${fx.id}`}>{s.home}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => bumpScore(fx.id, 'home', -1)}
                                data-testid={`home-down-${fx.id}`}
                              >
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                            </div>

                            <span className="text-xl text-slate-300">-</span>

                            <div className="flex flex-col items-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => bumpScore(fx.id, 'away', 1)}
                                data-testid={`away-up-${fx.id}`}
                              >
                                <ChevronUp className="w-4 h-4" />
                              </Button>
                              <span className="text-3xl font-bold text-[#0F172A] my-1" data-testid={`away-score-${fx.id}`}>{s.away}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => bumpScore(fx.id, 'away', -1)}
                                data-testid={`away-down-${fx.id}`}
                              >
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="text-center min-w-[110px]">
                            <p className={`text-base font-bold ${awayTeam === 'West Ham' ? 'text-[#7C2C3B]' : 'text-slate-700'}`} data-testid={`away-team-${fx.id}`}>
                              {awayTeam}
                            </p>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Away</p>
                          </div>
                        </div>

                        <div className="text-center mt-3">
                          <Button
                            onClick={() => submitPredictionFor(fx)}
                            disabled={s.submitting || !user}
                            className="bg-[#7C2C3B] hover:bg-[#5a1f2b]"
                            data-testid={`submit-prediction-${fx.id}`}
                          >
                            {s.submitting ? "Saving..." : (s.saved && !s.dirty ? "Update prediction" : "Submit prediction")}
                          </Button>
                          {!user && (
                            <p className="text-xs text-slate-500 mt-2">Log in to submit predictions</p>
                          )}
                          {s.message && (
                            <p className={`text-xs mt-2 ${s.message === "Saved!" ? 'text-green-600' : 'text-red-600'}`}>
                              {s.message}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-slate-600">
                      <strong>Points:</strong> Correct Score = <span className="text-green-600 font-bold">3 pts</span> |
                      Correct Result = <span className="text-blue-600 font-bold">1 pt</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                  SEASON LEADERBOARD
                </h2>
                
                <div className="flex gap-2">
                  <Input
                    placeholder="Search by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchUser()}
                    className="w-48"
                    data-testid="search-user-input"
                  />
                  <Button onClick={searchUser} variant="outline">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h3 className="font-bold text-slate-700 mb-2">Search Results:</h3>
                  {searchResults.map(result => (
                    <div key={result.user_id} className="flex items-center justify-between py-2">
                      <span className="font-medium">{result.user_name}</span>
                      <span className="text-sm">
                        Rank: <strong>#{result.rank}</strong> | Points: <strong>{result.total_points}</strong>
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Leaderboard Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Points</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Predictions</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Exact Scores</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Correct Results</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {leaderboard.map((entry) => (
                      <tr 
                        key={entry.user_id} 
                        className={`hover:bg-slate-50 ${user && entry.user_id === user.id ? 'bg-yellow-50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center w-8 h-8">
                            {getRankBadge(entry.rank)}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">
                          {entry.user_name}
                          {user && entry.user_id === user.id && (
                            <span className="ml-2 text-xs text-[#7C2C3B]">(You)</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-bold text-[#7C2C3B] text-lg">{entry.total_points}</span>
                        </td>
                        <td className="px-4 py-3 text-center text-slate-600">{entry.predictions_made}</td>
                        <td className="px-4 py-3 text-center text-green-600 font-medium">{entry.correct_scores}</td>
                        <td className="px-4 py-3 text-center text-blue-600 font-medium">{entry.correct_results}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {leaderboard.length === 0 && (
                <p className="text-center text-slate-500 py-8">No predictions yet this season. Be the first!</p>
              )}
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
                MY PREDICTIONS
              </h2>

              {!user ? (
                <p className="text-center text-slate-500 py-8">Please log in to view your prediction history</p>
              ) : myPredictions.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No predictions yet. Make your first prediction!</p>
              ) : (
                <div className="space-y-4">
                  {myPredictions.map(pred => {
                    const isHome = isHomeGame({
                      home_away: pred.fixture_home_away,
                      venue: pred.fixture_venue,
                    });
                    const homeTeam = isHome ? 'West Ham' : (pred.fixture_opponent || 'Unknown');
                    const awayTeam = isHome ? (pred.fixture_opponent || 'Unknown') : 'West Ham';
                    const fixtureDate = pred.fixture_date 
                      ? new Date(pred.fixture_date).toLocaleDateString('en-GB', { 
                          weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' 
                        })
                      : new Date(pred.created_at).toLocaleDateString('en-GB');
                    
                    return (
                      <div 
                        key={pred.id} 
                        className={`p-4 rounded-lg border ${
                          pred.points_earned === 3 ? 'border-green-300 bg-green-50' :
                          pred.points_earned === 1 ? 'border-blue-300 bg-blue-50' :
                          pred.actual_home_score !== null && pred.actual_home_score !== undefined ? 'border-red-300 bg-red-50' :
                          'border-slate-200 bg-slate-50'
                        }`}
                        data-testid="prediction-history-item"
                      >
                        {/* Match header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {pred.fixture_competition && (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#7C2C3B] text-white">
                                {pred.fixture_competition}
                              </span>
                            )}
                            <span className="text-xs text-slate-500">{fixtureDate}</span>
                          </div>
                          <div>
                            {pred.points_earned > 0 ? (
                              <span className={`text-lg font-bold ${
                                pred.points_earned === 3 ? 'text-green-600' : 'text-blue-600'
                              }`}>
                                +{pred.points_earned} pts
                              </span>
                            ) : pred.actual_home_score !== null && pred.actual_home_score !== undefined ? (
                              <span className="text-sm font-medium text-red-600">0 pts</span>
                            ) : (
                              <span className="text-sm text-slate-500 font-medium">Pending</span>
                            )}
                          </div>
                        </div>

                        {/* Teams and scores */}
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div className="flex-1 text-right">
                                <p className={`font-bold ${homeTeam === 'West Ham' ? 'text-[#7C2C3B]' : 'text-slate-700'}`}>
                                  {homeTeam}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 text-center min-w-[80px]">
                                <span className="text-xl font-bold text-slate-800">
                                  {pred.predicted_home_score}
                                </span>
                                <span className="text-slate-400">-</span>
                                <span className="text-xl font-bold text-slate-800">
                                  {pred.predicted_away_score}
                                </span>
                              </div>
                              <div className="flex-1 text-left">
                                <p className={`font-bold ${awayTeam === 'West Ham' ? 'text-[#7C2C3B]' : 'text-slate-700'}`}>
                                  {awayTeam}
                                </p>
                              </div>
                            </div>
                            
                            {pred.actual_home_score !== null && pred.actual_home_score !== undefined && (
                              <div className="flex items-center gap-3 mt-1">
                                <div className="flex-1 text-right">
                                  <span className="text-xs text-slate-500">Actual:</span>
                                </div>
                                <div className="flex items-center gap-2 text-center min-w-[80px]">
                                  <span className="text-sm font-semibold text-slate-600">
                                    {pred.actual_home_score}
                                  </span>
                                  <span className="text-slate-400 text-sm">-</span>
                                  <span className="text-sm font-semibold text-slate-600">
                                    {pred.actual_away_score}
                                  </span>
                                </div>
                                <div className="flex-1"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
}
